import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material"
import React, { useCallback, useContext, useMemo } from "react"
import { customPalette } from "../../../../../../../../../../../../theme"
import { IconCubeSpark } from "../../../../../../../../../../../components/custom-icons/icon-cube-spark"
import { SwitchLabelTypography } from "../../../../../../../../../../../components/typography/switch-label-typography"
import { BSMorphologyEnum } from "../../../../../../../../../../../core/enum/bsMorphologyEnum"
import { BSMorphologyDisplayer } from "../../../../../../tabs/BSBuildingTabs/BSBuildingMorphologyTab/components/BSMorphologyDisplayer"
import { BSMorphologyContext } from "../../../../../../tabs/BSBuildingTabs/BSBuildingMorphologyTab/context/BSMorphologyContext"
import { BSLevelContext } from "../../../context/BSLevelContext"
import { BSLevelFormContext } from "../../../context/BSLevelFormContext"
import { GroupLevel } from "../../../context/GroupLevel"
import { EditableLevelMorphology } from "./EditableLevelMorphology"
import { NonEditableLevelMorphology } from "./NonEditableLevelMorphology"

interface IProps {
  groupLevel: GroupLevel
}

export function MorphologyAccordion({ groupLevel }: Readonly<IProps>): React.JSX.Element {
  const { bsMorphology } = useContext(BSMorphologyContext)
  const { setIsLevelUpdated } = useContext(BSLevelContext)
  const { setFormFieldValue } = useContext(BSLevelFormContext)

  const isSuperstructure = useMemo(() => groupLevel.levelList[0].superstructure, [groupLevel.levelList])

  const isMorphologyOverridden = useMemo(() => groupLevel.levelList[0].morphologyOverridden, [groupLevel.levelList])

  const isRectangleShape = groupLevel.levelList[0].rectangleDimension !== undefined
  const isLShape = groupLevel.levelList[0].shapeLDimension !== undefined

  const onChangeSwitch: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void = useCallback(
    (_event, checked) => {
      setFormFieldValue("morphologyOverridden", checked, groupLevel.id, isSuperstructure)

      if (!checked && bsMorphology?.shape === BSMorphologyEnum.RECTANGLE) {
        setFormFieldValue("rectangleDimension", bsMorphology.rectangleDimension, groupLevel.id, isSuperstructure)
        setFormFieldValue("shapeLDimension", undefined, groupLevel.id, isSuperstructure)
      } else if (!checked && bsMorphology?.shape === BSMorphologyEnum.L) {
        setFormFieldValue("shapeLDimension", bsMorphology.shapeLDimension, groupLevel.id, isSuperstructure)
        setFormFieldValue("rectangleDimension", undefined, groupLevel.id, isSuperstructure)
      }

      setIsLevelUpdated(true)
    },
    [
      bsMorphology?.rectangleDimension,
      bsMorphology?.shape,
      bsMorphology?.shapeLDimension,
      groupLevel.id,
      isSuperstructure,
      setFormFieldValue,
      setIsLevelUpdated,
    ]
  )

  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: "14px",
        border: `solid 3px ${customPalette.lightGray7}`,
        backgroundColor: customPalette.lightGray7,
        "&:hover": {
          cursor: "pointer",
        },
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: customPalette.lightBlue2 }} />}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="body2" fontWeight={600}>
            Morphologie du groupe de niveaux
          </Typography>
          <IconCubeSpark sx={{ color: isMorphologyOverridden ? customPalette.mainGreen5 : customPalette.lightBlue2 }} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <FormControlLabel
          control={<Switch id="bim" checked={isMorphologyOverridden} onChange={onChangeSwitch} />}
          label={<SwitchLabelTypography label="Forme personnalisée" color={customPalette.lightBlue2} />}
        />
        <Grid container>
          {isMorphologyOverridden ? (
            <EditableLevelMorphology groupLevel={groupLevel} isRectangleShape={isRectangleShape} isLShape={isLShape} />
          ) : (
            bsMorphology && <NonEditableLevelMorphology morphology={bsMorphology} />
          )}

          <Grid item xs={6} display="flex" justifyContent="center">
            {!isMorphologyOverridden && bsMorphology && <BSMorphologyDisplayer shape={bsMorphology.shape} height="250px" />}

            {isMorphologyOverridden && isRectangleShape && (
              <BSMorphologyDisplayer shape={BSMorphologyEnum.RECTANGLE} height="250px" />
            )}

            {isMorphologyOverridden && isLShape && <BSMorphologyDisplayer shape={BSMorphologyEnum.L} height="250px" />}
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}
